import { useEffect, useState, type CSSProperties } from 'react';
import { apiRequest } from '../lib/api';

/**
 * A video behind a permission-gated route.
 *
 * A `<video>` element cannot send an Authorization header, and this API takes
 * authorisation from nowhere else. The fix used for images — fetch the bytes
 * and render a blob: URL — is wrong here: a blob has to download completely
 * before the first frame appears and cannot be seeked, which for an 80MB
 * walkthrough on a Kenyan mobile bundle means an unusable player.
 *
 * So the component asks an authenticated endpoint for a five-minute ticket
 * naming this one file, and points the element at a URL carrying it. Playback
 * is then an ordinary ranged GET: it starts in a second and the scrub bar
 * works.
 *
 * A ticket in a query string is a real tradeoff — it can land in a browser
 * history or a referrer — which is why it expires in five minutes, names a
 * single file, and grants nothing else.
 */
export function AuthedVideo({ ticketUrl, mediaUrl, className, style }: {
  ticketUrl: string; mediaUrl: string; className?: string; style?: CSSProperties;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSrc(null); setFailed(false);
    apiRequest<{ token: string }>(ticketUrl)
      .then((r) => { if (!cancelled) setSrc(`${mediaUrl}?t=${encodeURIComponent(r.token)}`); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [ticketUrl, mediaUrl]);

  if (failed) return <p className="muted">This video could not be loaded. Reload the page to try again.</p>;
  if (!src) return <p className="muted">Loading the video…</p>;

  return <video controls preload="metadata" playsInline className={className} style={style} src={src} />;
}
